import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import classnames from 'classnames';
import { getRoundedRating } from 'utils/rating';

import { useSparefootSelfStorageSchema } from 'decorators';
import { withOnVisibleFacilityHandler } from 'decorators/facility';

import { ViewportAction } from 'components/core/ViewportAction';
import { Group } from 'components/core/Page';
import { Text } from 'components/core/Typography';
import CancelSign from 'components/svg/CancelSign';


import HiddenFacilityCardDetails from './HiddenFacilityCardDetails';


import './HiddenFacilityCard.scss';

const withHiddenDerivedProps = (WrappedComponent) => {
	const WithDerivedProps = (props) => {
		const { facility, searchType, searchLocation } = props;
		const derivedProps = {
			location: facility.location, // city state zip
			color: facility.color,
			distance: `${facility.distance} miles away`,
			name: facility.name,
			average:
				facility.reviewMetadata &&
				getRoundedRating(facility.reviewMetadata.average),
			hasReviews:
				facility.reviewMetadata && facility.reviewMetadata.count > 0,
			facilityImage: facility.images[0],
			city: get(searchLocation, 'city'),
			state: get(searchLocation, 'state'),
			facilityZipCode: get(facility, 'location.zip'), //
			facilityUrl: facility.url.facility,
			noFollowFacilityLink: searchType === 'vehicle',
		};
		return (
			<WrappedComponent
				{...props}
				{...derivedProps}
			/>
		);
	};
	return WithDerivedProps;
};

const withHiddenHandlers = (WrappedComponent) => {
	const WithHandlers = (props) => {
		const fetchSearchCityPhoneNumber = () =>
			props.getLocalPhoneNumberByCityState(props.city, props.state);
		return (
			<WrappedComponent
				{...props}
				fetchSearchCityPhoneNumber={fetchSearchCityPhoneNumber}
			/>
		);
	};
	return WithHandlers;
};

export const enhance = (Component) =>
	withHiddenDerivedProps(
		withOnVisibleFacilityHandler(
			withHiddenHandlers(Component),
		),
	);

export function HiddenFacilityCard({
	location,
	distance,
	name,
	average,
	hasReviews,
	facilityImage,
	facilityUrl,
	noFollowFacilityLink,
	onVisible,
	fetchSearchCityPhoneNumber,
	percentVisible,
	lazyLoad = true,
	withSchema,
	selfStorageSchema: selfStorageSchemaProp,
	fullWidthCard,
	facility,
	baseUrl,
	units,
	reviewCount,
	...rest
}) {
	const schemaResult = useSparefootSelfStorageSchema({ facility, baseUrl, units, reviewCount, ...rest });
	const selfStorageSchema = selfStorageSchemaProp || schemaResult.selfStorageSchema;
	return (
		<ViewportAction onVisible={onVisible} percentVisible={percentVisible}>
			<div
				className={classnames('hidden-facility-card', { fullWidthCard })}
				itemScope
				itemType="https://schema.org/SelfStorage"
			>
				<Group className="hidden-facility-header">
					<CancelSign className="cancel-icon-svg" />
					<Text
						className="hidden-facility-header-text"
						size="smaller"
						weight="ultraBold"
						color="white"
						last
					>
						This facility is not currently accepting new reservations on SpareFoot
					</Text>
				</Group>

				<div className={classnames('hidden-facility-content')}>
					<HiddenFacilityCardDetails
						average={average}
						distance={distance}
						facilityUrl={facilityUrl}
						facilityImage={facilityImage}
						hasReviews={hasReviews}
						name={name}
						streetAddress={location.streetAddress}
						city={location.city}
						state={location.state}
						zip={location.zip}
						noFollowFacilityLink={noFollowFacilityLink}
						fetchSearchCityPhoneNumber={fetchSearchCityPhoneNumber}
						lazyLoad={lazyLoad}
					/>
				</div>
			</div>
			{withSchema && selfStorageSchema ? (
				<script
					key="schema"
					type="application/ld+json"
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{ __html: selfStorageSchema }}
				/>
			) : null}
		</ViewportAction>
	);
}
/* eslint-enable */

HiddenFacilityCard.propTypes = {
	location: PropTypes.object.isRequired,
	distance: PropTypes.string,
	name: PropTypes.string,
	average: PropTypes.number,
	hasReviews: PropTypes.bool,
	facilityImage: PropTypes.string,
	facilityUrl: PropTypes.string,
	noFollowFacilityLink: PropTypes.bool,
	fetchSearchCityPhoneNumber: PropTypes.func,
	onVisible: PropTypes.func,
	percentVisible: PropTypes.number.isRequired,
	lazyLoad: PropTypes.bool,
	withSchema: PropTypes.bool,
	selfStorageSchema: PropTypes.string,
	fullWidthCard: PropTypes.bool
};

export default enhance(HiddenFacilityCard);
